import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from "react-router-dom";
import privateApi from '../../apis/privateApi';
import Spinner from '../../components/Spinner/Spinner';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { FiEdit, FiTrash2 } from "react-icons/fi";
import './RoleDetail.css';

const RoleDetail = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const roleId = state?.role?._id;

    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // 🔥 Delete states
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState("");
    const [deleteId, setDeleteId] = useState(null);

    const generateToken = () =>
        Math.random().toString(36).substring(2, 8).toUpperCase();

    // Fetch role details
    const fetchRoleDetail = async (id) => {
        try {
            setLoading(true);
            const { data } = await privateApi.get(`/api/roles/${id}`);
            if (data.success) {
                setRole(data.data);
            } else {
                setError('Role not found.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch role details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!roleId) {
            setError('Role ID not provided.');
            setLoading(false);
            return;
        }
        fetchRoleDetail(roleId);
    }, [roleId]);

    // -------------------
    // Delete logic
    // -------------------
    const handleDeleteClick = (id) => {
        const token = generateToken();
        setDeleteId(id);
        setConfirmToken(token);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async (input) => {
        if (input !== confirmToken) {
            alert("Incorrect confirmation code.");
            setConfirmOpen(false);
            return;
        }

        try {
            await privateApi.delete(`/api/roles/${deleteId}`);
            navigate("/roles"); // 🔥 redirect after delete
        } catch (err) {
            alert(err.response?.data?.message || "Delete failed.");
        } finally {
            setConfirmOpen(false);
            setDeleteId(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmOpen(false);
        setDeleteId(null);
    };

    const formatLabel = (value) =>
        value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    // -------------------
    // UI STATES
    // -------------------

    if (loading) {
        return (
            <div className="role-detail-container">
                <Spinner loadingInfo="Loading role details..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="role-detail-container">
                <p className="role-detail-error">{error}</p>
                <button className="role-detail-back-btn" onClick={() => navigate(-1)}>
                    Back
                </button>
            </div>
        );
    }

    return (
        <div className="role-detail-container">
            <header className="role-detail-header">
                <h2>Role Details</h2>

                <div className="role-detail-actions">
                    <button
                        className="role-detail-back-btn"
                        onClick={() => navigate(-1)}
                    >
                        Back
                    </button>

                    <Link
                        to="/roles/edit"
                        state={{ role }}
                        className="edit-btn"
                    >
                        <FiEdit />
                    </Link>

                    <button
                        onClick={() => handleDeleteClick(role._id)}
                        className="delete-btn"
                    >
                        <FiTrash2 />
                    </button>
                </div>
            </header>

            <div className="role-detail-card">
                <div className="role-detail-item">
                    <strong>Name:</strong>
                    <span>{role.name}</span>
                </div>

                <div className="role-detail-item">
                    <strong>Description:</strong>
                    <span>{role.description || '-'}</span>
                </div>

                <div className="role-detail-item">
                    <strong>Level:</strong>
                    <span>{role.level}</span>
                </div>

                <div className="role-detail-item">
                    <strong>Allowed Budget Levels:</strong>
                    <span>
                        {role.allowedBudgetLevels?.map(formatLabel).join(', ') || '-'}
                    </span>
                </div>

                <div className="role-detail-item">
                    <strong>Permissions:</strong>
                    <span>
                        {role.permissions?.map(formatLabel).join(', ') || '-'}
                    </span>
                </div>
            </div>

            {/* 🔥 Confirm Dialog */}
            <ConfirmDialog
                show={confirmOpen}
                token={confirmToken}
                action="You are about to delete this role"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default RoleDetail;